import React, { type CSSProperties, type FC } from "react";
import { createPortal } from "react-dom";
import { confirm } from "@app/services/confirm";
import { OverflowMarquee } from "@shared/ui/overflow-marquee";
import { Icon } from "@src/shared/ui";
import { useScannerContext } from "./ScannerContext";
import { openManualBarcodeEntry } from "./manualBarcodeEntry/openManualBarcodeEntry";
import type {
    ScannerHintConfig,
    ScannerHintIndicatorConfig,
    ScannerHintPlacement,
    ScannerHintPosition,
    ScannerHintUiState,
} from "./types";
import "./ScannerHintOverlay.scss";

interface OverlayOffset {
    x: number;
    y: number;
}

interface DragState {
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startOffset: OverlayOffset;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    moved: boolean;
}

type ScannerHintOverlayStyle = CSSProperties & {
    "--scanner-hint-overlay-top"?: string;
    "--scanner-hint-overlay-right"?: string;
    "--scanner-hint-overlay-bottom"?: string;
    "--scanner-hint-overlay-left"?: string;
    "--scanner-hint-overlay-base-x"?: string;
    "--scanner-hint-overlay-base-y"?: string;
    "--scanner-hint-overlay-drag-x"?: string;
    "--scanner-hint-overlay-drag-y"?: string;
};

const DRAG_TAP_TOLERANCE_PX = 6;
const LONG_PRESS_MANUAL_ENTRY_MS = 650;
const HINT_CHANGED_PING_MS = 900;
const EDGE_INSET = "10px";
const BOTTOM_INSET = "calc(var(--layout-nav-height, 0px) + 10px)";
const DEFAULT_PLACEMENT: ScannerHintPlacement = "bottom-center";
const MANUAL_ENTRY_HINT_TITLE = "Долгое нажатие или ПКМ — ввести штрихкод вручную";
const DISMISS_CONFIRM_TITLE = "Скрыть подсказку сканирования?";
const DISMISS_CONFIRM_MESSAGE =
    "Подсказка будет скрыта до перезагрузки страницы. Сканирование продолжит работать без визуальной подсказки.";

function clampOffset(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function getHintAppearanceKey(hint?: ScannerHintConfig): string {
    if (hint == null) {
        return "";
    }

    const title = typeof hint.title === "string" ? hint.title : "";
    const description = typeof hint.description === "string" ? hint.description : "";
    const placement = hint.position?.placement ?? DEFAULT_PLACEMENT;
    const offsetX = hint.position?.offset?.x ?? 0;
    const offsetY = hint.position?.offset?.y ?? 0;
    const initialCollapsed = hint.initialCollapsed ?? false;
    const stateKey = hint.stateKey ?? "";

    return `${stateKey}|${title}|${description}|${placement}:${offsetX}:${offsetY}|${initialCollapsed}`;
}

function resolvePlacementStyle(position?: ScannerHintPosition): ScannerHintOverlayStyle {
    const placement = position?.placement ?? DEFAULT_PLACEMENT;
    const offsetX = position?.offset?.x ?? 0;
    const offsetY = position?.offset?.y ?? 0;
    const baseX = `${offsetX}px`;
    const baseY = `${offsetY}px`;

    switch (placement) {
        case "top-left":
            return {
                "--scanner-hint-overlay-top": EDGE_INSET,
                "--scanner-hint-overlay-right": "auto",
                "--scanner-hint-overlay-bottom": "auto",
                "--scanner-hint-overlay-left": EDGE_INSET,
                "--scanner-hint-overlay-base-x": baseX,
                "--scanner-hint-overlay-base-y": baseY,
            };
        case "top-center":
            return {
                "--scanner-hint-overlay-top": EDGE_INSET,
                "--scanner-hint-overlay-right": EDGE_INSET,
                "--scanner-hint-overlay-bottom": "auto",
                "--scanner-hint-overlay-left": EDGE_INSET,
                "--scanner-hint-overlay-base-x": baseX,
                "--scanner-hint-overlay-base-y": baseY,
            };
        case "top-right":
            return {
                "--scanner-hint-overlay-top": EDGE_INSET,
                "--scanner-hint-overlay-right": EDGE_INSET,
                "--scanner-hint-overlay-bottom": "auto",
                "--scanner-hint-overlay-left": "auto",
                "--scanner-hint-overlay-base-x": baseX,
                "--scanner-hint-overlay-base-y": baseY,
            };
        case "bottom-left":
            return {
                "--scanner-hint-overlay-top": "auto",
                "--scanner-hint-overlay-right": "auto",
                "--scanner-hint-overlay-bottom": BOTTOM_INSET,
                "--scanner-hint-overlay-left": EDGE_INSET,
                "--scanner-hint-overlay-base-x": baseX,
                "--scanner-hint-overlay-base-y": baseY,
            };
        case "bottom-right":
            return {
                "--scanner-hint-overlay-top": "auto",
                "--scanner-hint-overlay-right": EDGE_INSET,
                "--scanner-hint-overlay-bottom": BOTTOM_INSET,
                "--scanner-hint-overlay-left": "auto",
                "--scanner-hint-overlay-base-x": baseX,
                "--scanner-hint-overlay-base-y": baseY,
            };
        case "center":
            return {
                "--scanner-hint-overlay-top": "50%",
                "--scanner-hint-overlay-right": "auto",
                "--scanner-hint-overlay-bottom": "auto",
                "--scanner-hint-overlay-left": "50%",
                "--scanner-hint-overlay-base-x": `calc(-50% + ${offsetX}px)`,
                "--scanner-hint-overlay-base-y": `calc(-50% + ${offsetY}px)`,
            };
        case "bottom-center":
        default:
            return {
                "--scanner-hint-overlay-top": "auto",
                "--scanner-hint-overlay-right": EDGE_INSET,
                "--scanner-hint-overlay-bottom": BOTTOM_INSET,
                "--scanner-hint-overlay-left": EDGE_INSET,
                "--scanner-hint-overlay-base-x": baseX,
                "--scanner-hint-overlay-base-y": baseY,
            };
    }
}

function getClampedOffsetForElement(element: HTMLElement, offset: OverlayOffset): OverlayOffset {
    const rect = element.getBoundingClientRect();

    return {
        x: clampOffset(offset.x, offset.x - rect.left, offset.x + window.innerWidth - rect.right),
        y: clampOffset(offset.y, offset.y - rect.top, offset.y + window.innerHeight - rect.bottom),
    };
}

function renderIndicator(indicator?: ScannerHintIndicatorConfig): React.ReactNode {
    if (indicator?.variant === "custom") {
        return <span className="scanner_hint_overlay__icon">{indicator.icon}</span>;
    }

    if (indicator?.variant === "scanner") {
        return (
            <span className="scanner_hint_overlay__icon">
                <Icon src="/icons/common/scan.svg" />
            </span>
        );
    }

    return <span className="scanner_hint_overlay__dot" />;
}

/** App-level плашка активного сценария аппаратного сканирования. */
export const ScannerHintOverlay: FC = () => {
    const { hintUiState, status, updateActiveHintUiState, updateHintUiState, captureActiveScanTarget } = useScannerContext();
    const [hintChangedPing, setHintChangedPing] = React.useState(false);
    const overlayRef = React.useRef<HTMLDivElement | null>(null);
    const dragStateRef = React.useRef<DragState | null>(null);
    const longPressTimerRef = React.useRef<number | null>(null);
    const suppressNextClickRef = React.useRef(false);
    const previousHintRef = React.useRef<ScannerHintConfig | undefined>(undefined);
    const previousAppearanceKeyRef = React.useRef<string | undefined>(undefined);

    const placement = status.hint?.position?.placement ?? DEFAULT_PLACEMENT;
    const appearanceKey = getHintAppearanceKey(status.hint);
    const collapsed = hintUiState?.collapsed ?? status.hint?.initialCollapsed ?? false;
    const dragOffset = hintUiState?.dragOffset ?? { x: 0, y: 0 };
    const currentHintUiState: ScannerHintUiState = hintUiState ?? {
        collapsed,
        dragOffset,
        dismissed: false,
    };

    React.useLayoutEffect(() => {
        if (!status.active) {
            setHintChangedPing(false);
            previousHintRef.current = undefined;
            previousAppearanceKeyRef.current = undefined;
            return;
        }

        if (previousAppearanceKeyRef.current !== appearanceKey) {
            previousAppearanceKeyRef.current = appearanceKey;
        }
    }, [appearanceKey, status.active]);

    React.useEffect(() => {
        if (!status.active) {
            return;
        }

        const previousHint = previousHintRef.current;
        previousHintRef.current = status.hint;

        if (collapsed !== true) {
            setHintChangedPing(false);
            return;
        }

        if (previousHint == null || previousHint === status.hint) {
            return;
        }

        setHintChangedPing(true);
        const timerId = window.setTimeout(() => {
            setHintChangedPing(false);
        }, HINT_CHANGED_PING_MS);

        return () => {
            window.clearTimeout(timerId);
        };
    }, [collapsed, status.active, status.hint]);

    const handleDismiss = React.useCallback(() => {
        const hintStateKey = status.hintStateKey;
        if (hintStateKey == null) {
            return;
        }

        void confirm
            .show({
                title: DISMISS_CONFIRM_TITLE,
                message: DISMISS_CONFIRM_MESSAGE,
                confirmText: "Скрыть",
                cancelText: "Оставить",
            })
            .then((confirmed) => {
                if (confirmed === true) {
                    updateHintUiState(hintStateKey, currentHintUiState, (currentState: ScannerHintUiState) => ({
                        ...currentState,
                        dismissed: true,
                    }));
                }
            });
    }, [currentHintUiState, status.hintStateKey, updateHintUiState]);

    const handleOpenManualBarcodeEntry = React.useCallback(() => {
        const scanTarget = captureActiveScanTarget();
        if (scanTarget == null) {
            return;
        }

        openManualBarcodeEntry(scanTarget);
    }, [captureActiveScanTarget]);

    const handleContextMenu = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
            handleOpenManualBarcodeEntry();
        },
        [handleOpenManualBarcodeEntry],
    );

    const clearLongPressTimer = React.useCallback(() => {
        const timerId = longPressTimerRef.current;
        if (timerId == null) {
            return;
        }

        window.clearTimeout(timerId);
        longPressTimerRef.current = null;
    }, []);

    React.useLayoutEffect(() => {
        const overlay = overlayRef.current;
        if (overlay == null) {
            return;
        }

        const clampCurrentOffset = (): void => {
            updateActiveHintUiState((currentState: ScannerHintUiState) => ({
                ...currentState,
                dragOffset: getClampedOffsetForElement(overlay, currentState.dragOffset),
            }));
        };

        const frameId = window.requestAnimationFrame(clampCurrentOffset);
        const observer = new ResizeObserver(clampCurrentOffset);
        observer.observe(overlay);
        window.addEventListener("resize", clampCurrentOffset);

        return () => {
            window.cancelAnimationFrame(frameId);
            observer.disconnect();
            window.removeEventListener("resize", clampCurrentOffset);
        };
    }, [appearanceKey, collapsed, updateActiveHintUiState]);

    React.useEffect(() => clearLongPressTimer, [clearLongPressTimer]);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }

        const overlay = overlayRef.current;
        if (overlay == null) {
            return;
        }

        const rect = overlay.getBoundingClientRect();
        dragStateRef.current = {
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startOffset: dragOffset,
            minX: dragOffset.x - rect.left,
            maxX: dragOffset.x + window.innerWidth - rect.right,
            minY: dragOffset.y - rect.top,
            maxY: dragOffset.y + window.innerHeight - rect.bottom,
            moved: false,
        };

        if (event.pointerType === "touch" || event.pointerType === "pen") {
            clearLongPressTimer();
            longPressTimerRef.current = window.setTimeout(() => {
                dragStateRef.current = null;
                longPressTimerRef.current = null;
                suppressNextClickRef.current = true;
                handleOpenManualBarcodeEntry();
            }, LONG_PRESS_MANUAL_ENTRY_MS);
        }

        overlay.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = dragStateRef.current;
        if (dragState == null || dragState.pointerId !== event.pointerId) {
            return;
        }

        const deltaX = event.clientX - dragState.startClientX;
        const deltaY = event.clientY - dragState.startClientY;
        const moved =
            dragState.moved || Math.abs(deltaX) > DRAG_TAP_TOLERANCE_PX || Math.abs(deltaY) > DRAG_TAP_TOLERANCE_PX;

        if (moved) {
            event.preventDefault();
            clearLongPressTimer();
            dragState.moved = true;
            updateActiveHintUiState((currentState: ScannerHintUiState) => ({
                ...currentState,
                dragOffset: {
                    x: clampOffset(dragState.startOffset.x + deltaX, dragState.minX, dragState.maxX),
                    y: clampOffset(dragState.startOffset.y + deltaY, dragState.minY, dragState.maxY),
                },
            }));
        }
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = dragStateRef.current;
        if (dragState == null || dragState.pointerId !== event.pointerId) {
            return;
        }

        dragStateRef.current = null;
        clearLongPressTimer();
        suppressNextClickRef.current = dragState.moved;

        const overlay = overlayRef.current;
        if (overlay?.hasPointerCapture(event.pointerId) === true) {
            overlay.releasePointerCapture(event.pointerId);
        }

        if (dragState.moved) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const handleToggleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        event.preventDefault();
        updateActiveHintUiState((currentState: ScannerHintUiState) => ({
            ...currentState,
            collapsed: currentState.collapsed !== true,
        }));
    };

    const handleDismissClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        handleDismiss();
    };

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
        }

        updateActiveHintUiState((currentState: ScannerHintUiState) => ({
            ...currentState,
            collapsed: currentState.collapsed !== true,
        }));
    };

    if (!status.active || status.hint == null || hintUiState?.dismissed === true) {
        return null;
    }

    const overlayStyle: ScannerHintOverlayStyle = {
        ...resolvePlacementStyle(status.hint.position),
        "--scanner-hint-overlay-drag-x": `${dragOffset.x}px`,
        "--scanner-hint-overlay-drag-y": `${dragOffset.y}px`,
    };
    const indicatorVariant = status.hint.indicator?.variant ?? "dot";

    return createPortal(
        <div
            ref={overlayRef}
            className="scanner_hint_overlay"
            role="button"
            tabIndex={0}
            title={MANUAL_ENTRY_HINT_TITLE}
            aria-label={collapsed === true ? "Развернуть подсказку сканирования" : "Свернуть подсказку сканирования"}
            aria-expanded={collapsed !== true}
            data-busy={status.busy}
            data-collapsed={collapsed}
            data-hint-changed={hintChangedPing}
            data-indicator={indicatorVariant}
            data-placement={placement}
            style={overlayStyle}
            onContextMenu={handleContextMenu}
            onKeyDown={handleToggleKeyDown}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
        >
            <div className="scanner_hint_overlay__indicator" aria-hidden="true">
                {renderIndicator(status.hint.indicator)}
            </div>
            {collapsed !== true && (
                <>
                    <div className="scanner_hint_overlay__content" role="status" aria-live="polite">
                        <div className="scanner_hint_overlay__title">
                            <OverflowMarquee>{status.hint.title}</OverflowMarquee>
                        </div>
                        {status.hint.description != null && (
                            <div className="scanner_hint_overlay__description">
                                <OverflowMarquee>{status.hint.description}</OverflowMarquee>
                            </div>
                        )}
                    </div>
                    <button
                        className="scanner_hint_overlay__dismiss"
                        type="button"
                        aria-label="Скрыть подсказку сканирования"
                        title="Скрыть подсказку"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={handleDismissClick}
                    >
                        ×
                    </button>
                </>
            )}
        </div>,
        document.body,
    );
};
